/**
 * Pure predicates for the global Cmd/Ctrl+K listener (#168) — extracted so the guard logic is unit
 * tested without a DOM/render (the host hook that wires them is thin glue).
 */

/** The subset of a keyboard event the hotkey test needs (so tests pass a plain object). */
export interface HotkeyEvent {
	key: string;
	metaKey: boolean;
	ctrlKey: boolean;
	altKey: boolean;
	shiftKey: boolean;
}

/**
 * True for the palette chord: ⌘K (macOS) or Ctrl+K (Win/Linux), with NO Alt/Shift so it never
 * collides with a browser or app shortcut that adds another modifier.
 */
export function isPaletteHotkey(event: HotkeyEvent): boolean {
	return (
		(event.metaKey || event.ctrlKey) &&
		!event.altKey &&
		!event.shiftKey &&
		event.key.toLowerCase() === "k"
	);
}

/**
 * True when focus is in a text-entry surface (input / textarea / select / contentEditable), where
 * Cmd+K must NOT be hijacked. Duck-typed (reads `tagName` / `isContentEditable`) so it works across
 * document realms and is trivially unit-testable.
 */
export function isTypingTarget(target: EventTarget | null): boolean {
	const element = target as {tagName?: unknown; isContentEditable?: unknown} | null;
	if (element == null || typeof element.tagName !== "string") {
		return false;
	}
	if (element.isContentEditable === true) {
		return true;
	}
	const tag = element.tagName.toUpperCase();
	return tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT";
}

/**
 * True when a genuinely modal dialog is already open — its own focus trap / Escape handling must
 * win, so the palette hotkey stands down. Mirrors the selector `FeaturePanel` already relies on.
 */
export function isModalOpen(doc: Document = document): boolean {
	return doc.querySelector('[role="dialog"][aria-modal="true"]') !== null;
}
